import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ReportBanner = () => {
  const navigate = useNavigate();
  // State to track visibility
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = React.useRef(null);

  useEffect(() => {
    // console.log"Component mounted");
    
    // Use intersection observer for production
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // console.log"Banner is intersecting viewport");
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    // For testing, you can uncomment this to force visibility
    // setTimeout(() => {
    //   setIsVisible(true);
    //   // console.log"Visibility set to true after timeout");
    // }, 500);

    return () => {
      if (bannerRef.current) {
        observer.disconnect();
      }
      // console.log"Component unmounted");
    };
  }, []);

  // Log every render with current visibility state
  // console.log`Rendering banner, visibility state: ${isVisible}`);

  return (
    <div 
      ref={bannerRef}
      style={{
        maxWidth: '1200px',
        margin: '80px auto',
        padding: '40px 30px',
        backgroundColor: 'white',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
      }}
    >
      {/* Left accent border */}
      <div 
        style={{
          position: 'absolute',
          height: '100%',
          width: '4px',
          left: 0,
          top: 0,
          background: 'linear-gradient(to bottom, #1a2a6c, #4286f4)',
          transform: isVisible ? 'scaleY(1)' : 'scaleY(0)',
          transformOrigin: 'top',
          transition: 'transform 0.6s ease',
          transitionDelay: '0.2s'
        }}
      ></div>
      
      {/* Background shapes */}
      <div
        style={{
          position: 'absolute',
          borderRadius: '50%',
          background: 'rgba(26, 42, 108, 0.05)',
          right: '-50px',
          bottom: '-50px',
          width: '200px',
          height: '200px',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 1s ease',
          transitionDelay: '0.5s',
          zIndex: 0
        }}
      ></div>
      
      <div
        style={{
          position: 'absolute',
          borderRadius: '50%',
          background: 'rgba(26, 42, 108, 0.05)',
          right: '100px',
          top: '-30px',
          width: '100px',
          height: '100px',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 1s ease',
          transitionDelay: '0.7s',
          zIndex: 0
        }}
      ></div>
      
      {/* Content wrapper */}
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '800px',
          position: 'relative',
          zIndex: 2
        }}
      >
        {/* Heading with animation */}
        <h1 
          style={{
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#000000',
            marginBottom: '16px',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
            transitionDelay: '0.3s'
          }}
        >
          PlasticAnalytics: An online plastic spectral library and analytical suite
        </h1>
        
        {/* Description with animation */}
        <p 
          style={{
            fontSize: '1.1rem',
            lineHeight: 1.6,
            color: '#000000',
            marginBottom: '32px',
            maxWidth: '700px',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
            transitionDelay: '0.5s'
          }}
        >
          Our research in Environmental Science & Technology introduces a consolidated spectral library and machine learning framework for standardised plastic analysis, providing significant improvements in prediction speed and accuracy.
        </p>
        
        {/* Link container with animation */}
        <div 
          style={{
            marginTop: '16px',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
            transitionDelay: '0.7s'
          }}
        >
          <a 
            style={{
              color: '#1a2a6c',
              backgroundColor: 'transparent',
              fontWeight: 600,
              fontSize: '1rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '10px 20px',
              borderRadius: '30px',
              border: '2px solid #1a2a6c',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            target="_blank"
            rel="noopener noreferrer"
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#1a2a6c';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#1a2a6c';
            }}
            onClick={() => navigate('/sign-in')}
          >
            Read the Publication <span style={{ marginLeft: '8px', transition: 'transform 0.3s ease' }}>→</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ReportBanner;